/* Bravo-101 · web v2. Formboard wiring (the page's one authored motion) and the demo form.
   Every feature is progressive: without this file the content is complete and visible. */
(() => {
  const root = document.documentElement;
  root.classList.add('bx-js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const WIRE = {
    blue: 'var(--bx-blue)',
    ink: '#3d454d',
    yellow: 'var(--bx-yellow)',
    green: 'var(--bx-green)',
    red: 'var(--bx-red)',
  };

  /* ---------- Formboard: spec rows wired to sequence steps through one tied bundle ---------- */
  class Formboard {
    constructor(el) {
      this.el = el;
      this.grid = el.querySelector('.bx-formboard__grid');
      this.svg = el.querySelector('.bx-formboard__svg');
      this.pairs = [...el.querySelectorAll('.bx-pair')];
      this.animate = el.dataset.animate === 'true' && !reduceMotion.matches;
      this.drawn = !this.animate;
      if (!this.grid || !this.svg || !this.pairs.length) return;
      this.render = this.render.bind(this);
      new ResizeObserver(() => this.render()).observe(this.grid);
      if (this.animate) this.arm();
      this.render();
    }

    path(d, cls, color, width) {
      const p = document.createElementNS(SVG_NS, 'path');
      p.setAttribute('d', d);
      p.setAttribute('class', cls);
      p.style.stroke = color;
      if (width) p.style.strokeWidth = width;
      return p;
    }

    render() {
      const svg = this.svg;
      if (getComputedStyle(svg).display === 'none') return;
      const box = this.grid.getBoundingClientRect();
      svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
      svg.replaceChildren();
      const n = this.pairs.length;
      const ends = this.pairs.map((pair) => {
        const a = pair.querySelector('.bx-spec-row').getBoundingClientRect();
        const b = pair.querySelector('.bx-step').getBoundingClientRect();
        return {
          x1: a.right - box.left,
          y1: a.top + a.height / 2 - box.top,
          x2: b.left - box.left,
          y2: b.top + b.height / 2 - box.top,
        };
      });
      const mid = ends.reduce((s, e) => s + e.y1 + e.y2, 0) / (2 * n);
      const x1 = Math.max(...ends.map((e) => e.x1));
      const x2 = Math.min(...ends.map((e) => e.x2));
      const span = x2 - x1;
      const xa = x1 + span * 0.32;
      const xb = x1 + span * 0.68;
      const gap = 7;

      ends.forEach((e, i) => {
        const ym = mid + (i - (n - 1) / 2) * gap;
        const d =
          `M ${e.x1} ${e.y1} C ${e.x1 + span * 0.2} ${e.y1}, ${xa - span * 0.14} ${ym}, ${xa} ${ym} ` +
          `L ${xb} ${ym} C ${xb + span * 0.14} ${ym}, ${e.x2 - span * 0.2} ${e.y2}, ${e.x2} ${e.y2}`;
        const color = WIRE[this.pairs[i].dataset.wire] || WIRE.ink;
        const g = document.createElementNS(SVG_NS, 'g');
        const edge = this.path(d, 'bx-wire', '#111418', '6');
        const core = this.path(d, 'bx-wire', color, '3.5');
        g.append(edge, core);
        const end = document.createElementNS(SVG_NS, 'circle');
        end.setAttribute('cx', e.x2);
        end.setAttribute('cy', e.y2);
        end.setAttribute('r', '4.5');
        end.setAttribute('class', 'bx-terminal');
        end.style.stroke = '#111418';
        g.append(end);
        svg.append(g);
        this.pairs[i]._wire = { g, edge, core };
        if (!this.drawn && !this.pairs[i].classList.contains('is-done')) this.hide(this.pairs[i]);
      });

      const tieX = (xa + xb) / 2 - 5;
      const tieH = n * gap + 12;
      for (const dx of [-span * 0.12, span * 0.12]) {
        const tie = document.createElementNS(SVG_NS, 'rect');
        tie.setAttribute('x', tieX + dx);
        tie.setAttribute('y', mid - tieH / 2);
        tie.setAttribute('width', '10');
        tie.setAttribute('height', tieH);
        tie.setAttribute('rx', '2');
        tie.setAttribute('class', 'bx-tie');
        svg.append(tie);
      }
    }

    hide(pair) {
      const { g, edge, core } = pair._wire;
      for (const p of [edge, core]) {
        const len = p.getTotalLength();
        p.style.strokeDasharray = `${len}`;
        p.style.strokeDashoffset = `${len}`;
        p.setAttribute('data-draw', '');
      }
      g.querySelector('circle').style.opacity = '0';
    }

    show(pair) {
      const { g, edge, core } = pair._wire;
      for (const p of [edge, core]) p.style.strokeDashoffset = '0';
      g.querySelector('circle').style.opacity = '1';
    }

    arm() {
      this.el.classList.add('bx-anim');
      for (const pair of this.pairs) pair.classList.add('is-waiting');
      const start = () => this.play();
      if (!('IntersectionObserver' in window)) return start();
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          start();
        }
      }, { threshold: 0.35 });
      io.observe(this.grid);
    }

    async play() {
      const wait = (ms) => new Promise((r) => setTimeout(r, ms));
      await wait(250);
      for (const pair of this.pairs) {
        pair.classList.add('is-live');
        await wait(350);
        if (pair._wire) this.show(pair);
        await wait(700);
        pair.classList.remove('is-waiting');
        pair.classList.add('is-done');
        await wait(450);
        pair.classList.remove('is-live');
      }
      this.drawn = true;
    }
  }

  /* ---------- Demo form: accessible validation on top of native constraints ---------- */
  function initForms() {
    for (const form of document.querySelectorAll('[data-bx-form] form')) {
      const summary = form.querySelector('[data-bx-summary]');
      form.setAttribute('novalidate', '');
      form.addEventListener('submit', (event) => {
        let first = null;
        for (const field of form.querySelectorAll('[data-bx-validate]')) {
          const error = form.querySelector(`#${field.getAttribute('aria-describedby')?.split(' ')[0]}`);
          let message = '';
          if (field.validity.valueMissing) message = field.dataset.msgRequired;
          else if (field.validity.typeMismatch) message = field.dataset.msgType;
          field.setAttribute('aria-invalid', message ? 'true' : 'false');
          if (error) error.textContent = message;
          if (message && !first) first = field;
        }
        for (const group of form.querySelectorAll('[data-bx-required-group]')) {
          const checked = group.querySelector('input:checked');
          const error = group.querySelector('[data-bx-group-error]');
          const message = checked ? '' : group.dataset.msgRequired;
          if (error) error.textContent = message;
          group.toggleAttribute('data-invalid', !!message);
          if (message && !first) first = group.querySelector('input');
        }
        if (first) {
          event.preventDefault();
          if (summary) {
            summary.hidden = false;
            summary.focus();
          } else {
            first.focus();
          }
          return;
        }
        // Customer form: role, company type and company travel as customer tags.
        const tagsInput = form.querySelector('[data-bx-tags]');
        if (tagsInput) {
          const base = tagsInput.dataset.base || tagsInput.value;
          tagsInput.dataset.base = base;
          const tags = [base];
          for (const el of form.querySelectorAll('[data-bx-tag]:checked')) tags.push(el.value);
          for (const el of form.querySelectorAll('[data-bx-tag-prefix]')) {
            const v = el.value.replace(/,/g, ' ').trim().slice(0, 80);
            if (v) tags.push(el.dataset.bxTagPrefix + v);
          }
          tagsInput.value = tags.join(', ');
        }
        const button = form.querySelector('[type="submit"]');
        if (button && button.dataset.sending) {
          button.setAttribute('aria-disabled', 'true');
          button.textContent = button.dataset.sending;
        }
      });
    }
  }

  /* ---------- v2.1: type preview, silent product loops, index previews ---------- */
  function initTypePreview() {
    const tipo = new URLSearchParams(location.search).get('tipo');
    if (tipo === 'producto' || tipo === 'corporativa') root.setAttribute('data-bx-type', tipo);
  }

  function initVideos() {
    const loops = [...document.querySelectorAll('video[data-hx-autoplay]')];
    if (!loops.length || reduceMotion.matches) return;
    const play = (v) => v.play().catch(() => {});
    const inIndex = (v) => v.closest('.hx-idx__preview');
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (inIndex(e.target)) continue;
          if (e.isIntersecting) play(e.target);
          else e.target.pause();
        }
      }, { threshold: 0.25 });
      for (const v of loops) if (!inIndex(v)) io.observe(v);
    }
    for (const item of document.querySelectorAll('.hx-idx')) {
      const v = item.querySelector('video[data-hx-autoplay]');
      if (!v) continue;
      item.addEventListener('pointerenter', () => play(v));
      item.addEventListener('pointerleave', () => v.pause());
      item.addEventListener('focusin', () => play(v));
      item.addEventListener('focusout', () => v.pause());
    }
  }

  /* ---------- Themis workflow animation (snippets/hx-flow.liquid) ----------
     Five steps; the scene carries at1…atN (accumulated). Step 1 is server-rendered, so the
     first frame is never blank. Autoplays only while on screen and never with
     prefers-reduced-motion; the step buttons always work. */
  const FLOW_MS = [3200, 6200, 4600, 3600, 5200];
  function initFlow(root) {
    const scene = root.querySelector('[data-hx-flow-scene]');
    const texts = root.querySelectorAll('[data-hx-flow-text]');
    const goBtns = root.querySelectorAll('[data-hx-flow-go]');
    const playBtn = root.querySelector('[data-hx-flow-play]');
    if (!scene || !playBtn) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let step = 1;
    let wantPlay = !reduce;
    let onScreen = false;
    let timer = null;
    let pinned = false;

    const show = (n) => {
      step = n;
      for (let i = 1; i <= 5; i++) scene.classList.toggle('at' + i, i <= n);
      for (const t of texts) {
        const on = Number(t.dataset.hxFlowText) === n;
        t.classList.toggle('is-active', on);
        if (on) t.removeAttribute('aria-hidden'); else t.setAttribute('aria-hidden', 'true');
      }
      for (const b of goBtns) b.setAttribute('aria-pressed', String(Number(b.dataset.hxFlowGo) === n));
    };
    const label = () => {
      const playing = wantPlay;
      playBtn.textContent = playing ? playBtn.dataset.labelPause : playBtn.dataset.labelPlay;
      playBtn.setAttribute('aria-pressed', String(playing));
    };
    const stop = () => { clearTimeout(timer); timer = null; };
    const schedule = () => {
      stop();
      if (pinned || !wantPlay || !onScreen || document.hidden) return;
      timer = setTimeout(() => {
        if (step >= 5) {
          for (let i = 1; i <= 5; i++) scene.classList.remove('at' + i);
          timer = setTimeout(() => { show(1); schedule(); }, 700);
          return;
        }
        show(step + 1);
        schedule();
      }, FLOW_MS[step - 1]);
    };

    for (const b of goBtns) b.addEventListener('click', () => { show(Number(b.dataset.hxFlowGo)); schedule(); });
    playBtn.addEventListener('click', () => { wantPlay = !wantPlay; label(); schedule(); });
    document.addEventListener('visibilitychange', schedule);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        for (const e of entries) { onScreen = e.isIntersecting; schedule(); }
      }, { threshold: 0.3 }).observe(root.querySelector('.hx-flow__stage') || root);
    } else {
      onScreen = true;
    }
    show(1);
    label();
    schedule();
    // Used by initFlowPin: while pinned the scroll drives the steps and the clock stops.
    root.hxFlow = { show, setPinned(p) { pinned = p; if (p) stop(); else schedule(); } };
  }

  /* ---------- Scroll container: Horizon scrolls .page-wrapper, not the window ---------- */
  function getScroller() {
    const pw = document.querySelector('.page-wrapper');
    return pw && /(auto|scroll)/.test(getComputedStyle(pw).overflowY) ? pw : window;
  }
  function onScroll(fn) {
    const sc = getScroller();
    let queued = false;
    const run = () => { queued = false; fn(); };
    sc.addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(run); } }, { passive: true });
    window.addEventListener('resize', fn);
    return sc;
  }
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp01 = (v) => Math.min(1, Math.max(0, v));

  /* ---------- Sections pinned to the scroll (CSS sticky + scroll progress) ----------
     The section grows by `run` screens; its inner block is sticky under the header and the
     scroll inside it advances steps 1…n; after the last step the page simply continues.
     Native sticky keeps the block perfectly still (a transform-based pin bounces inside
     Horizon's .page-wrapper scroller). Desktop with motion only. */
  function pinSteps(sec, { n, run, mq, onStep, onToggle, weights }) {
    // weights: share of the scroll each step takes (default: equal). Cumulative ends 0..1.
    const w = weights && weights.length === n ? weights : Array(n).fill(1);
    const sum = w.reduce((a, b) => a + b, 0);
    const ends = w.map((_, i) => w.slice(0, i + 1).reduce((a, b) => a + b, 0) / sum);
    let on = false;
    let current = 0;
    const hdr = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hdr')) || 0;
    const range = () => Math.max(1, sec.offsetHeight - (window.innerHeight - hdr()));
    const progress = () => clamp01((hdr() - sec.getBoundingClientRect().top) / range());
    const update = () => {
      if (!on) return;
      const p = progress();
      const k = Math.min(n, ends.findIndex((e) => p < e) + 1 || n);
      if (k !== current) { current = k; onStep(k); }
    };
    const apply = () => {
      const want = mq.matches && !REDUCE;
      if (want === on) return;
      on = want;
      sec.classList.toggle('is-pinned', on);
      if (on) sec.style.setProperty('--pin-run', String(run)); else sec.style.removeProperty('--pin-run');
      current = 0;
      onToggle(on);
      update();
    };
    const sc = onScroll(update);
    mq.addEventListener('change', apply);
    apply();
    return {
      isOn: () => on,
      goTo(k) {
        const base = sc === window ? window.scrollY : sc.scrollTop;
        const top = sec.getBoundingClientRect().top - (sc === window ? 0 : sc.getBoundingClientRect().top);
        const mid = ((k > 1 ? ends[k - 2] : 0) + ends[k - 1]) / 2;
        sc.scrollTo({ top: base + top - hdr() + mid * range(), behavior: 'smooth' });
      },
    };
  }

  /* «Cómo funciona» pinned: the scroll advances steps 1–5; the step buttons scroll there. */
  function initFlowPin(sec) {
    const root = sec.querySelector('[data-hx-flow]');
    if (!root || !root.hxFlow) return;
    const api = root.hxFlow;
    const pin = pinSteps(sec, {
      n: 5, run: 3.6, weights: [1, 2, 1, 1, 1], mq: window.matchMedia('(min-width: 1100px) and (min-height: 640px)'),
      onStep: (k) => api.show(k),
      onToggle: (p) => api.setPinned(p),
    });
    for (const b of root.querySelectorAll('[data-hx-flow-go]')) {
      b.addEventListener('click', () => { if (pin.isOn()) pin.goTo(Number(b.dataset.hxFlowGo)); });
    }
  }

  /* ---------- «La familia Bravo-101»: one datum travels the family ----------
     Pinned from 990×640 with motion allowed; the scroll advances the five stops and moves
     the yellow tag. Phones, short screens or reduced motion: every stop complete, still. */
  function initFamily(sec) {
    const stops = [...sec.querySelectorAll('[data-hx-fam-stop]')];
    const token = sec.querySelector('[data-hx-fam-token]');
    const track = sec.querySelector('.hx-fam__track');
    if (REDUCE || stops.length < 2 || !token || !track) return;
    const n = stops.length;
    const go = (k) => {
      const i = k - 1;
      stops.forEach((s, j) => { s.classList.toggle('is-reached', j <= i); s.classList.toggle('is-current', j === i); });
      token.textContent = stops[i].dataset.tag || '';
      track.style.setProperty('--x', ((i + 0.5) / n) * 100 + '%');
      track.style.setProperty('--p', String(i / (n - 1)));
    };
    pinSteps(sec, {
      n, run: 3, mq: window.matchMedia('(min-width: 990px) and (min-height: 640px)'),
      onStep: go,
      onToggle: (p) => {
        if (p) {
          // Stops are visible while pinned: the one-at-a-time reveal does not apply here.
          stops.forEach((s) => { s.classList.remove('is-pending'); s.classList.add('is-in'); });
          go(1);
        } else {
          stops.forEach((s) => s.classList.remove('is-reached', 'is-current'));
          track.style.removeProperty('--p');
        }
      },
    });
  }

  /* ---------- Statement that lights up word by word (once, scroll-linked) ---------- */
  const norm = (w) => w.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');
  function initLight(sec) {
    const list = (s) => (s || '').split(',').map(norm).filter(Boolean);
    const tags = list(sec.dataset.tagWords);
    const cables = list(sec.dataset.cableWords);
    const words = [];
    for (const part of sec.querySelectorAll('.hx-statement__lead, .hx-statement__muted')) {
      const txt = part.textContent;
      part.textContent = '';
      for (const tok of txt.split(/(\s+)/)) {
        if (!tok) continue;
        if (/^\s+$/.test(tok)) { part.append(tok); continue; }
        const n = norm(tok);
        const sp = document.createElement('span');
        if (tags.includes(n) || cables.includes(n)) {
          const m = tok.match(/^(.*?)([.,;:]*)$/);
          sp.className = 'hx-w ' + (tags.includes(n) ? 'hx-w--tag' : 'hx-w--cable');
          sp.textContent = m[1];
          part.append(sp);
          if (m[2]) part.append(m[2]);
        } else {
          sp.className = 'hx-w';
          sp.textContent = tok;
          part.append(sp);
        }
        words.push(sp);
      }
    }
    if (REDUCE || !words.length) return;
    sec.classList.add('is-lighting');
    let done = false;
    const text = sec.querySelector('.hx-statement__text');
    const update = () => {
      if (done) return;
      const r = text.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = clamp01((vh * 0.9 - r.top) / (vh * 0.55));
      const lit = Math.round(p * words.length);
      words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
      if (lit >= words.length) done = true;
    };
    onScroll(update);
    update();
  }

  /* ---------- Rows and cards that enter one at a time (once) ---------- */
  function initReveal() {
    if (REDUCE || !('IntersectionObserver' in window)) return;
    const vh = window.innerHeight;
    const io = new IntersectionObserver((entries) => {
      const ins = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      ins.forEach((e, i) => {
        e.target.style.setProperty('--ri', String(i));
        e.target.classList.add('is-in');
        e.target.classList.remove('is-pending');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12 });
    for (const el of document.querySelectorAll('[data-hx-reveal] [data-hx-reveal-item]')) {
      if (el.getBoundingClientRect().top < vh * 0.92) continue;
      el.classList.add('is-pending');
      io.observe(el);
    }
  }

  /* ---------- "On this page" index for reading pages ---------- */
  function initToc(nav) {
    const body = nav.parentElement && nav.parentElement.querySelector('.bx-rte');
    if (!body) return;
    const hs = [...body.querySelectorAll('h2')].filter((h) => h.textContent.trim());
    if (hs.length < 2) return;
    const ol = nav.querySelector('ol');
    const links = hs.map((h, i) => {
      if (!h.id) h.id = 'seccion-' + (i + 1) + '-' + norm(h.textContent).slice(0, 32);
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent.trim();
      li.append(a);
      ol.append(li);
      return a;
    });
    nav.hidden = false;
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const i = hs.indexOf(e.target);
          links.forEach((a, k) => a.setAttribute('aria-current', String(k === i)));
        }
      }, { rootMargin: '0px 0px -70% 0px' });
      hs.forEach((h) => io.observe(h));
    }
  }

  /* ---------- Hero piece «Desglose» (snippets/hx-breakdown.liquid) ----------
     Data light up one by one; from each one a cable (measured from the DOM, behind the
     cards) runs to its result card; the gap lands on the question card; then «Trazable»
     and the step. Alternates the two statements, pauses off screen, «Repetir» restarts.
     Reduced motion: the first statement complete and still. Below 900 px: no cables. */
  const SVGNS = 'http://www.w3.org/2000/svg';
  function initBreakdown(root) {
    if (REDUCE) return;
    const sets = [...root.querySelectorAll('[data-hx-bd-set]')];
    const svg = root.querySelector('.hx-bd__lines');
    const stage = root.querySelector('.hx-bd__stage');
    const replay = root.querySelector('[data-hx-bd-replay]');
    if (!sets.length || !svg || !stage) return;
    let idx = 0;
    let timers = [];
    let onScreen = false;
    const at = (ms, fn) => timers.push(setTimeout(fn, ms));
    const stop = () => { timers.forEach(clearTimeout); timers = []; };
    const parts = (set) => ({ src: [...set.querySelectorAll('[data-hx-bd-src]')], cards: [...set.querySelectorAll('[data-hx-bd-card]')] });

    const draw = () => {
      svg.textContent = '';
      if (getComputedStyle(svg).display === 'none') return;
      const set = sets[idx];
      const { src, cards } = parts(set);
      const b = stage.getBoundingClientRect();
      src.forEach((el, i) => {
        const card = cards[i];
        if (!card) return;
        const rs = el.getClientRects();
        const r = rs[rs.length - 1] || el.getBoundingClientRect();
        const c = card.getBoundingClientRect();
        const x1 = r.left + r.width / 2 - b.left;
        const y1 = r.bottom - b.top + 2;
        const x2 = c.left + Math.min(c.width / 2, 40) - b.left;
        const y2 = c.top - b.top + 2;
        const my = (y1 + y2) / 2;
        const p = document.createElementNS(SVGNS, 'path');
        p.setAttribute('d', `M${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}`);
        p.setAttribute('stroke', getComputedStyle(card).getPropertyValue('--bd-c').trim() || '#7fb0ee');
        svg.append(p);
        p.style.setProperty('--len', String(Math.ceil(p.getTotalLength())));
        if (card.classList.contains('is-on')) p.classList.add('is-on');
      });
    };
    const reset = (set) => {
      const { src, cards } = parts(set);
      [...src, ...cards].forEach((e) => e.classList.remove('is-on'));
    };
    const showAll = (set) => {
      const { src, cards } = parts(set);
      [...src, ...cards].forEach((e) => e.classList.add('is-on'));
      draw();
    };
    const setCurrent = (i) => {
      idx = i;
      sets.forEach((s, k) => {
        s.classList.toggle('is-current', k === i);
        if (k === i) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
      });
    };
    const play = () => {
      stop();
      const set = sets[idx];
      reset(set);
      const { src, cards } = parts(set);
      draw();
      const lines = () => [...svg.children];
      src.forEach((el, i) => at(500 + i * 800, () => {
        el.classList.add('is-on');
        at(300, () => { if (cards[i]) cards[i].classList.add('is-on'); const l = lines()[i]; if (l) l.classList.add('is-on'); });
      }));
      const end = 500 + src.length * 800;
      for (let k = src.length; k < cards.length; k++) at(end + (k - src.length) * 600, () => cards[k].classList.add('is-on'));
      at(end + (cards.length - src.length) * 600 + 5200, () => {
        if (sets.length < 2) return;
        setCurrent((idx + 1) % sets.length);
        play();
      });
    };
    root.classList.add('is-anim');
    if (replay) {
      replay.hidden = false;
      replay.addEventListener('click', () => { setCurrent(0); play(); });
    }
    if ('ResizeObserver' in window) new ResizeObserver(() => requestAnimationFrame(draw)).observe(stage);
    else window.addEventListener('resize', () => requestAnimationFrame(draw));
    const visible = () => onScreen && !document.hidden;
    const sync = () => { if (visible()) play(); else { stop(); showAll(sets[idx]); } };
    document.addEventListener('visibilitychange', sync);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        for (const e of entries) { if (e.isIntersecting === onScreen) continue; onScreen = e.isIntersecting; sync(); }
      }, { threshold: 0.25 }).observe(root);
    } else {
      onScreen = true;
      sync();
    }
    // First frame: the whole line and every card, until it starts on screen.
    showAll(sets[idx]);
  }

  /* ---------- «Qué necesitas y cómo se conecta»: six steps over the diagram ----------
     Plays only while on screen; buttons 1–6 and pause always work. Reduced motion or no
     JS: every node and connection lit, the first step text shown, nothing advances. */
  const CN_STEPS = [
    ['you', 'ai', 'e1', 'l1'],
    ['ai', 'cli', 'e3', 'l3'],
    ['cli', 'fw', 'e4'],
    ['you', 'app', 'fw', 'e2', 'e5', 'l2', 'l5'],
    ['fw', 'lv', 'ts', 'e6', 'e7', 'l6', 'l7'],
    ['ts', 'st', 'e8', 'l8'],
  ];
  function initConnect(sec) {
    const all = [...sec.querySelectorAll('[data-cn]')];
    const texts = sec.querySelectorAll('[data-hx-cn-text]');
    const goBtns = sec.querySelectorAll('[data-hx-cn-go]');
    const playBtn = sec.querySelector('[data-hx-cn-play]');
    if (REDUCE) { all.forEach((e) => e.classList.add('is-on')); return; }
    // Animated mode: what is not in the current step dims (CSS keys on .is-anim).
    sec.classList.add('is-anim');
    let step = 1;
    let wantPlay = true;
    let onScreen = false;
    let timer = null;
    const show = (n) => {
      step = n;
      const on = CN_STEPS[n - 1];
      all.forEach((e) => e.classList.toggle('is-on', on.includes(e.dataset.cn)));
      for (const t of texts) {
        const a = Number(t.dataset.hxCnText) === n;
        t.classList.toggle('is-active', a);
        if (a) t.removeAttribute('aria-hidden'); else t.setAttribute('aria-hidden', 'true');
      }
      for (const b of goBtns) b.setAttribute('aria-pressed', String(Number(b.dataset.hxCnGo) === n));
    };
    const label = () => {
      if (!playBtn) return;
      playBtn.textContent = wantPlay ? playBtn.dataset.labelPause : playBtn.dataset.labelPlay;
      playBtn.setAttribute('aria-pressed', String(wantPlay));
    };
    const schedule = () => {
      clearTimeout(timer);
      // The flowing dashes only run while the piece is visible and playing.
      sec.classList.toggle('is-paused', !wantPlay || !onScreen || document.hidden);
      if (!wantPlay || !onScreen || document.hidden) return;
      timer = setTimeout(() => { show(step % CN_STEPS.length + 1); schedule(); }, 4200);
    };
    for (const b of goBtns) b.addEventListener('click', () => { show(Number(b.dataset.hxCnGo)); schedule(); });
    if (playBtn) {
      playBtn.hidden = false;
      playBtn.addEventListener('click', () => { wantPlay = !wantPlay; label(); schedule(); });
    }
    document.addEventListener('visibilitychange', schedule);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        for (const e of entries) { onScreen = e.isIntersecting; schedule(); }
      }, { threshold: 0.3 }).observe(sec.querySelector('.hx-cn__stage') || sec);
    } else {
      onScreen = true;
    }
    show(1);
    label();
    schedule();

    // Pinned (Braulio, 2026-10-14): the scroll advances the six steps; buttons scroll there.
    if (!sec.hasAttribute('data-hx-cn-pin')) return;
    let pinned = false;
    const pin = pinSteps(sec, {
      n: CN_STEPS.length, run: 3, mq: window.matchMedia('(min-width: 1100px) and (min-height: 640px)'),
      onStep: (k) => show(k),
      onToggle: (p) => { pinned = p; wantPlay = !p; label(); schedule(); },
    });
    for (const b of goBtns) b.addEventListener('click', () => { if (pinned) pin.goTo(Number(b.dataset.hxCnGo)); });
  }

  function init() {
    initTypePreview();
    initVideos();
    for (const el of document.querySelectorAll('[data-hx-flow]')) initFlow(el);
    for (const el of document.querySelectorAll('[data-hx-flow-pin]')) initFlowPin(el);
    for (const el of document.querySelectorAll('[data-hx-light]')) initLight(el);
    for (const el of document.querySelectorAll('[data-hx-bd]')) initBreakdown(el);
    for (const el of document.querySelectorAll('[data-hx-fam]')) initFamily(el);
    for (const el of document.querySelectorAll('[data-hx-cn]')) initConnect(el);
    for (const el of document.querySelectorAll('[data-hx-toc]')) initToc(el);
    initReveal();
    for (const el of document.querySelectorAll('.bx-formboard')) new Formboard(el);
    initForms();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
